// =============================================================================
// Log Collector Context and Provider
// =============================================================================

import { createContext, useContext, useMemo, useCallback, useState, useRef, type ReactNode } from 'react';
import { ApiClient, type ApiClientConfig } from '../lib/api.js';

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = 'log-collector-token';

// =============================================================================
// Helpers
// =============================================================================

/**
 * Clear stored token from localStorage.
 */
function clearStoredToken(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Get the current URL path + search + hash for return_to.
 */
function getCurrentPath(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname + window.location.search + window.location.hash;
}

// =============================================================================
// Types
// =============================================================================

export interface LogCollectorContextValue {
  /** The API client instance */
  client: ApiClient;
  /** Base URL for the API */
  baseUrl: string;
  /** Whether a 401 forced logout (session expired) */
  isSessionExpired: boolean;
  /** Force logout - clears token and triggers auth state update */
  forceLogout: () => void;
  /** Reset session expired state (called after successful login) */
  resetSessionExpired: () => void;
}

export interface LogCollectorProviderProps {
  /** Child components */
  children: ReactNode;
  /** Base URL for the log-collector API */
  baseUrl?: string;
  /** Initial session token (optional) */
  sessionToken?: string;
  /** Initial API key (optional) */
  apiKey?: string;
  /** Callback when session becomes unauthorized (401 from server) */
  onUnauthorized?: (returnTo: string) => void;
}

// =============================================================================
// Context
// =============================================================================

const LogCollectorContext = createContext<LogCollectorContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

/**
 * Provider component for log-collector API access.
 *
 * @example
 * ```tsx
 * import { LogCollectorProvider } from '@log-collector/ui/hooks';
 *
 * function App() {
 *   return (
 *     <LogCollectorProvider baseUrl="/api">
 *       <Dashboard />
 *     </LogCollectorProvider>
 *   );
 * }
 * ```
 */
export function LogCollectorProvider({
  children,
  baseUrl = '/api',
  sessionToken,
  apiKey,
  onUnauthorized,
}: LogCollectorProviderProps): JSX.Element {
  // Track if session was expired by 401
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  // Track last login time to ignore stale 401s from in-flight requests
  const lastLoginTimeRef = useRef<number>(0);

  // Force logout handler - clears token and optionally triggers callback
  const forceLogout = useCallback(() => {
    // Ignore 401s that arrive within 2 seconds of a successful login
    // These are likely stale responses from requests started before login
    const timeSinceLogin = Date.now() - lastLoginTimeRef.current;
    if (timeSinceLogin < 2000) {
      return;
    }

    // Immediately mark session as expired so UI can react
    setIsSessionExpired(true);
    clearStoredToken();
    if (onUnauthorized) {
      const returnTo = getCurrentPath();
      onUnauthorized(returnTo);
    }
    // Note: We don't reload immediately anymore - let React handle the state change
    // The isSessionExpired state will cause the UI to show login form
  }, [onUnauthorized]);

  // Reset session expired state (called after successful login)
  const resetSessionExpired = useCallback(() => {
    lastLoginTimeRef.current = Date.now();
    setIsSessionExpired(false);
  }, []);

  const client = useMemo(() => {
    const config: ApiClientConfig = {
      baseUrl,
      sessionToken,
      apiKey,
      onUnauthorized: forceLogout,
    };
    return new ApiClient(config);
  }, [baseUrl, sessionToken, apiKey, forceLogout]);

  const value = useMemo<LogCollectorContextValue>(
    () => ({ client, baseUrl, isSessionExpired, forceLogout, resetSessionExpired }),
    [client, baseUrl, isSessionExpired, forceLogout, resetSessionExpired]
  );

  return (
    <LogCollectorContext.Provider value={value}>
      {children}
    </LogCollectorContext.Provider>
  );
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to access the log-collector context.
 * Must be used within a LogCollectorProvider.
 *
 * @throws Error if used outside of LogCollectorProvider
 */
export function useLogCollectorContext(): LogCollectorContextValue {
  const context = useContext(LogCollectorContext);

  if (!context) {
    throw new Error(
      'useLogCollectorContext must be used within a LogCollectorProvider'
    );
  }

  return context;
}

/**
 * Hook to safely check if session is expired.
 * Returns false if not within a provider.
 */
export function useIsSessionExpired(): boolean {
  const context = useContext(LogCollectorContext);
  return context?.isSessionExpired ?? false;
}

/**
 * Hook to get session expired state and reset function.
 * Returns noop reset if not within a provider.
 */
export function useSessionExpiredState(): { isSessionExpired: boolean; resetSessionExpired: () => void } {
  const context = useContext(LogCollectorContext);
  return {
    isSessionExpired: context?.isSessionExpired ?? false,
    resetSessionExpired: context?.resetSessionExpired ?? (() => {}),
  };
}
